import { SEGMENT_KEYS } from "@granular/fabric3-definitions";
import { AnalyticsBrowser } from "@segment/analytics-next";
import { getEnvironment } from "./environment";

const analytics = new AnalyticsBrowser();

/**
 * On localhost, route every segment call through a `console.debug` stub
 * instead of posting to Segment. Engineers can validate the calls they
 * expect are firing without polluting the Dev source. Evaluated lazily —
 * never at module load — so importing this module in non-browser contexts
 * (workers, etc.) doesn't throw on `globalThis.location`.
 *
 * Uses `getEnvironment(false)` so a developer who points their local app at
 * a deployed backend via `window.RUNTIME_ENVIRONMENT` still gets debug
 * logging — they're still running on localhost physically.
 */
function isLocalhostRuntime(): boolean {
  if (globalThis.location === undefined) return false;
  return getEnvironment(false) === "local";
}

export function loadAnalytics() {
  if (isLocalhostRuntime()) {
    if (process.env.NODE_ENV !== "test") {
      console.debug(
        "[segment] localhost detected — skipping analytics.load(); calls will be logged, not sent",
      );
    }
    return;
  }
  analytics.load(
    { writeKey: SEGMENT_KEYS[getEnvironment()] },
    {
      // Disable analytics in tests
      disable: process.env.NODE_ENV === "test",
    },
  );
}

/**
 * All AnalyticsBrowser methods that emit events and therefore must be held
 * until the user has been identified.
 */
type BufferedMethodName =
  | "track"
  | "page"
  | "screen"
  | "pageView"
  | "trackSubmit"
  | "trackClick"
  | "trackLink"
  | "alias"
  | "group";

const BUFFERED_METHODS: ReadonlySet<string> = new Set<BufferedMethodName>([
  "track",
  "page",
  "screen",
  "pageView",
  "trackSubmit",
  "trackClick",
  "trackLink",
  "alias",
  "group",
]);

type BufferedCall = {
  [M in BufferedMethodName]: {
    method: M;
    args: Parameters<AnalyticsBrowser[M]>;
  };
}[BufferedMethodName];

/**
 * Buffer tracking calls until identify() has been called by the Segment
 * component. This prevents anonymous events during the window between eager
 * analytics.load() and the lazy Segment component mounting.
 */
let identified = false;
const preIdentifyBuffer: BufferedCall[] = [];

function isBufferedMethod(name: string | symbol): name is BufferedMethodName {
  return typeof name === "string" && BUFFERED_METHODS.has(name);
}

export function markIdentified(): void {
  identified = true;
  // On localhost the Proxy short-circuits and never pushes to the buffer,
  // so there's nothing to flush. Skip touching the unloaded analytics instance.
  if (isLocalhostRuntime()) return;

  for (const call of preIdentifyBuffer) {
    // TypeScript cannot narrow a discriminated union in a generic loop, so we
    // use an intermediate typed record to dispatch safely without `any`.
    type Dispatch = {
      [M in BufferedMethodName]: (
        ...args: Parameters<AnalyticsBrowser[M]>
      ) => ReturnType<AnalyticsBrowser[M]>;
    };
    const dispatch = analytics as Dispatch;
    const fn = dispatch[call.method] as (...a: unknown[]) => unknown;
    fn(...call.args);
  }
  preIdentifyBuffer.length = 0;
}

export const segment = new Proxy<AnalyticsBrowser>(analytics, {
  get(target, prop, receiver) {
    if (typeof prop === "string" && isLocalhostRuntime()) {
      const value = Reflect.get(target, prop, receiver) as unknown;
      if (typeof value === "function") {
        return (...args: unknown[]) => {
          if (process.env.NODE_ENV !== "test") {
            console.debug(`[segment] ${prop}`, ...args);
          }
          return Promise.resolve();
        };
      }
      return value;
    }
    if (!identified && isBufferedMethod(prop)) {
      const method = prop;
      return (...args: Parameters<AnalyticsBrowser[typeof method]>) => {
        preIdentifyBuffer.push({ method, args } as BufferedCall);
      };
    }
    const value = Reflect.get(target, prop, receiver) as unknown;
    if (typeof value === "function") {
      return (value as (...a: unknown[]) => unknown).bind(target);
    }
    return value;
  },
});
